import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { wheelZoomPlugin, applySyncRange } from "@/lib/uplot-zoom";

// SparklineUplot — minimal uPlot lifecycle wrapper for per-tile mini-charts.
//
// Owns: host element + ResizeObserver, uPlot init/destroy, optional
// dashed-nominal-line plugin, optional shared-zoom wiring, optional click
// handler keyed by the hovered data index.
//
// Does NOT own: data shape, series config (colors / scatter-vs-line),
// axis formatting, legend rendering. Those stay per-consumer because they
// vary too much (PQ scatter swimlanes vs three-phase amp sparkline vs
// future waveform-style charts) to share without rebuilding a config
// system from scratch.
//
// Re-init policy: the underlying uPlot instance is rebuilt when `data`
// changes by reference, when xRange/yRange/height change, or when series
// length changes. The `nominal` value is read via a ref-getter, so updates
// to it redraw the dashed line without rebuilding uPlot.

function nominalLinePlugin(
  getNominal: () => number | null,
  color: string,
): uPlot.Plugin {
  return {
    hooks: {
      drawClear: (u) => {
        const nom = getNominal();
        if (nom == null || nom <= 0) return;
        const yPos = u.valToPos(nom, "y", true);
        const top = u.bbox.top;
        const height = u.bbox.height;
        if (yPos < top || yPos > top + height) return;
        const ctx = u.ctx;
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(u.bbox.left, yPos);
        ctx.lineTo(u.bbox.left + u.bbox.width, yPos);
        ctx.stroke();
        ctx.restore();
      },
    },
  };
}

export interface SparklineUplotProps {
  /** uPlot-shaped data: [xValues, ...ySeriesValues]. */
  data: uPlot.AlignedData;
  /** uPlot series config, including the implicit time series at index 0. */
  series: uPlot.Series[];
  /** X-axis range in seconds-since-epoch (uPlot expects seconds for time scales). */
  xRange: [number, number];
  /** Y-axis range, or "auto" to let uPlot auto-fit. */
  yRange: [number, number] | "auto";
  /** Chart height in px. */
  height?: number;
  /** Dashed reference line at this Y-value (e.g., nominal current, service capacity). */
  nominal?: number | null;
  /** Stroke color for the dashed nominal line. Defaults to emerald-500/50. */
  nominalColor?: string;
  /** External zoom range to apply, for cross-instance sync. */
  syncRange?: [number, number] | null;
  /** Fires when this instance's zoom changes (for sync fan-out). */
  onSyncRangeChange?: (range: [number, number]) => void;
  /** Click handler: called with the data index under the cursor at click time. */
  onIndexClick?: (idx: number) => void;
  /** uPlot axes config (full passthrough). When omitted, no axes render. */
  axes?: uPlot.Axis[];
  /** Show uPlot's built-in legend. Defaults to false (sparklines hide it). */
  showLegend?: boolean;
  /** Extra uPlot plugins to layer on. */
  extraPlugins?: uPlot.Plugin[];
}

export function SparklineUplot({
  data,
  series,
  xRange,
  yRange,
  height = 110,
  nominal = null,
  nominalColor = "rgba(34, 197, 94, 0.5)",
  syncRange = null,
  onSyncRangeChange,
  onIndexClick,
  axes,
  showLegend = false,
  extraPlugins,
}: SparklineUplotProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const uRef = useRef<uPlot | null>(null);

  // Refs for values that should update without rebuilding uPlot.
  const nominalRef = useRef<number | null>(nominal);
  nominalRef.current = nominal;
  const onSyncRef = useRef(onSyncRangeChange);
  onSyncRef.current = onSyncRangeChange;
  const onClickRef = useRef(onIndexClick);
  onClickRef.current = onIndexClick;

  useEffect(() => {
    if (!hostRef.current) return;

    const opts: uPlot.Options = {
      width: hostRef.current.clientWidth,
      height,
      scales: {
        x: { time: true, min: xRange[0], max: xRange[1] },
        y: yRange === "auto"
          ? { auto: true }
          : { auto: false, range: [yRange[0], yRange[1]] },
      },
      series,
      axes: axes ?? [],
      plugins: [
        nominalLinePlugin(() => nominalRef.current, nominalColor),
        wheelZoomPlugin({
          factor: 0.85,
          onChange: (range) => onSyncRef.current?.(range),
        }),
        ...(extraPlugins ?? []),
      ],
      cursor: { drag: { setScale: false } },
      select: { show: false, left: 0, top: 0, width: 0, height: 0 },
      legend: { show: showLegend },
    };

    const u = new uPlot(opts, data, hostRef.current);
    uRef.current = u;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      uRef.current?.setSize({ width: w, height });
    });
    ro.observe(hostRef.current);

    const onClick = () => {
      const cur = uRef.current;
      const handler = onClickRef.current;
      if (!cur || !handler) return;
      const idx = cur.cursor.idx;
      if (idx == null || idx < 0) return;
      handler(idx);
    };
    u.over.addEventListener("click", onClick);

    return () => {
      ro.disconnect();
      u.over.removeEventListener("click", onClick);
      u.destroy();
      uRef.current = null;
    };
    // axes/series/extraPlugins are passed by reference; consumers control identity.
    // Rebuild on xRange/yRange/height/data/series-length/nominalColor changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, series.length, xRange[0], xRange[1],
      yRange === "auto" ? "auto" : `${yRange[0]},${yRange[1]}`,
      height, nominalColor]);

  // Apply external sync range without rebuilding.
  useEffect(() => applySyncRange(uRef.current, syncRange), [syncRange]);

  return <div ref={hostRef} style={{ width: "100%", height }} />;
}
